import { useState, useRef, useEffect, useCallback, useMemo } from "react";

const EMPTY_EVENT_ALLOW_SELECTORS = [];

/**
 * 表格行选择与鼠标拖动选择的自定义Hook
 *
 * ⚠️ 重要提醒：使用此Hook时必须完整配置以下属性才能正常显示选择状态样式：
 * 1. 解构时必须包含 getRowProps: const { selectedRowKeys, rowSelection, getIndexColumn, getRowProps } = useTableSelection({...})
 * 2. Table组件必须添加 onRow 属性: <Table onRow={getRowProps} ... />
 * 3. 缺少任何一个都会导致选择状态的视觉反馈失效（序号列外框、角标、行高亮等）
 *
 * 正确使用示例参考：frontend/src/features/flightSchedule/OfficialList/ScheduleOfficialList.jsx
 *
 * @param {Object} options 配置选项
 * @param {string} options.rowKey 行数据的唯一标识字段，如'id'、'code'等
 * @param {Function} options.getRowKey 自定义获取行键值的函数，用于复合主键，如(record) => `${record.departure}-${record.arrival}`
 * @param {Array} options.dataSource 表格数据源
 * @param {Object} options.pagination 分页配置
 * @param {boolean} options.enableGlobalTracking 是否启用全局鼠标跟踪（默认true）
 * @param {number} options.mouseMoveThrottle 鼠标移动节流时间（毫秒，默认50）
 * @param {Function} options.onSelectionChange 选择变化回调，当选中行变化时触发，参数为 (selectedRowKeys, selectedRows)
 * @param {Function} options.isRowSelectionDisabled 判断行是否禁止选择，参数为 (record)
 * @returns {Object} 包含行选择相关状态和方法的对象
 */
const useTableSelection = (options) => {
    const { rowKey = "id", getRowKey, dataSource = [], pagination = { current: 1, pageSize: 10 }, enableGlobalTracking = true, mouseMoveThrottle = 50, onSelectionChange, isRowSelectionDisabled, eventAllowSelectors = EMPTY_EVENT_ALLOW_SELECTORS } = options || {};

    // 使用useMemo记忆化处理dataSource。
    // 依赖于 dataSource 引用本身，当引用变化时（例如，数据被筛选或重新获取），
    // 所有依赖于 memoizedDataSource 的 effect 和 callback 都会同步更新。
    // 旧的依赖 [dataSource.length, dataSource[0]?.id] 在某些筛选场景下无法正确触发更新，导致bug。
    const memoizedDataSource = useMemo(() => dataSource, [dataSource]);

    // 状态定义
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isSelecting, setIsSelecting] = useState(false);
    const [startIndex, setStartIndex] = useState(null);
    const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
    const tableRef = useRef(null);
    const throttleTimerRef = useRef(null);
    const lastExecutionTimeRef = useRef(0);
    const currentSelectionRef = useRef(null); // 用于存储当前选择操作的参考数据
    const rowsCache = useRef(new Map()); // 缓存行数据的key映射，提高查找效率
    const rafIdRef = useRef(null); // 用于存储requestAnimationFrame ID
    const isInitialMountRef = useRef(true); // 用于跳过初始渲染的回调触发
    const onSelectionChangeRef = useRef(onSelectionChange); // 使用 Ref 存储回调，避免依赖更新问题

    // 同步 onSelectionChange 到 Ref
    useEffect(() => {
        onSelectionChangeRef.current = onSelectionChange;
    }, [onSelectionChange]);

    // 选择变化时触发回调
    useEffect(() => {
        // 跳过初始渲染
        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            return;
        }
        // 触发回调
        if (onSelectionChangeRef.current) {
            onSelectionChangeRef.current(selectedRowKeys, selectedRows);
        }
    }, [selectedRowKeys, selectedRows]);

    // 在dataSource变化时更新缓存
    useEffect(() => {
        rowsCache.current.clear();
        memoizedDataSource.forEach((item, index) => {
            const key = getRecordKey(item, index);
            rowsCache.current.set(String(key), { index, item });
        });
    }, [memoizedDataSource]);

    // 获取行的key值
    const getRecordKey = useCallback(
        (record, index) => {
            if (typeof getRowKey === "function") {
                return getRowKey(record, index);
            }
            return record[rowKey];
        },
        [getRowKey, rowKey],
    );

    const isRecordSelectionDisabled = useCallback(
        (record) => (typeof isRowSelectionDisabled === "function" ? Boolean(isRowSelectionDisabled(record)) : false),
        [isRowSelectionDisabled],
    );

    // 处理多选变化 - 使用requestAnimationFrame优化渲染性能
    const handleRowSelectionChange = useCallback((selectedKeys, selected) => {
        const nextKeys = Array.isArray(selectedKeys) ? selectedKeys : [];
        const nextRows = Array.isArray(selected) ? selected : [];
        const allowedRows = nextRows.filter((record) => !isRecordSelectionDisabled(record));
        const allowedKeySet = new Set(allowedRows.map((record) => String(getRecordKey(record))));
        const allowedKeys = nextKeys.filter((key) => allowedKeySet.has(String(key)));

        // 取消前一个动画帧
        if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current);
        }

        // 使用requestAnimationFrame优化UI更新
        rafIdRef.current = requestAnimationFrame(() => {
            setSelectedRowKeys(allowedKeys);
            setSelectedRows(allowedRows);
            rafIdRef.current = null;
        });
    }, [getRecordKey, isRecordSelectionDisabled]);

    useEffect(() => {
        if (typeof isRowSelectionDisabled !== "function" || selectedRowKeys.length === 0) return;

        const disabledKeys = new Set(
            memoizedDataSource
                .filter((item) => isRecordSelectionDisabled(item))
                .map((item) => String(getRecordKey(item))),
        );
        if (disabledKeys.size === 0) return;

        const nextSelectedKeys = selectedRowKeys.filter((key) => !disabledKeys.has(String(key)));
        if (nextSelectedKeys.length === selectedRowKeys.length) return;

        setSelectedRowKeys(nextSelectedKeys);
        setSelectedRows((prevRows) => prevRows.filter((row) => !disabledKeys.has(String(getRecordKey(row)))));
    }, [getRecordKey, isRecordSelectionDisabled, isRowSelectionDisabled, memoizedDataSource, selectedRowKeys]);

    // 增加对弹出层事件的判断
    const shouldHandleEvent = useCallback((event) => {
        const target = event.target;
        if (!target?.closest) return true;

        const isExplicitlyAllowed = eventAllowSelectors.some((selector) => target.closest(selector));
        if (isExplicitlyAllowed) return true;

        // 检查事件源是否在弹出层内
        const isInPopover = target.closest(".ant-popover") || target.closest(".ant-modal") || target.closest(".ant-select-dropdown") || target.closest(".ant-picker-dropdown");

        // 如果事件源在弹出层内，返回false表示不处理该事件
        return !isInPopover;
    }, [eventAllowSelectors]);

    // 修改鼠标按下事件处理函数
    const handleMouseDown = useCallback(
        (event, index) => {
            // 如果事件源在弹出层内，不处理该事件
            if (!shouldHandleEvent(event)) {
                return;
            }

            if (event.button === 0) {
                const record = memoizedDataSource[index];
                if (record && isRecordSelectionDisabled(record)) return;

                // 只处理左键点击
                setIsSelecting(true);
                setStartIndex(index);
                setLastSelectedIndex(index);

                // 存储当前选择操作的起始状态
                currentSelectionRef.current = {
                    startIndex: index,
                    existingSelection: [...selectedRowKeys],
                    existingRows: [...selectedRows],
                };

                // 只在序号列或特定选择区域阻止默认的文本选择行为
                // 检查是否是序号列的鼠标事件
                if (event.target.closest(".row-number-with-checkbox")) {
                    event.preventDefault();
                }
            }
        },
        [selectedRowKeys, selectedRows, shouldHandleEvent, memoizedDataSource, isRecordSelectionDisabled],
    );

    // 防止节流重复执行 - 使用节流优化性能
    const throttle = useCallback(
        (callback, delay = mouseMoveThrottle) => {
            const now = Date.now();
            if (throttleTimerRef.current) {
                clearTimeout(throttleTimerRef.current);
            }

            if (now - lastExecutionTimeRef.current >= delay) {
                lastExecutionTimeRef.current = now;
                callback();
            } else {
                throttleTimerRef.current = setTimeout(() => {
                    throttleTimerRef.current = null;
                    lastExecutionTimeRef.current = Date.now();
                    callback();
                }, delay);
            }
        },
        [mouseMoveThrottle],
    );

    // 优化鼠标移动事件处理函数
    const handleMouseMove = useCallback(
        (event, index) => {
            if (!isSelecting || index === lastSelectedIndex) return;

            const hoveredRecord = memoizedDataSource[index];
            if (hoveredRecord && isRecordSelectionDisabled(hoveredRecord)) return;

            // 如果事件源在弹出层内，不处理该事件
            if (!shouldHandleEvent(event)) {
                return;
            }

            throttle(() => {
                setLastSelectedIndex(index);

                // 计算选择范围
                const startIdx = Math.min(startIndex, index);
                const endIdx = Math.max(startIndex, index);

                // 快速获取范围内的键，避免不必要的映射操作
                const keysInRange = [];
                for (let i = startIdx; i <= endIdx; i++) {
                    if (i < memoizedDataSource.length) {
                        const item = memoizedDataSource[i];
                        if (isRecordSelectionDisabled(item)) continue;
                        keysInRange.push(getRecordKey(item, i));
                    }
                }

                // 使用Set操作提高合并效率
                const existingSelection = new Set(currentSelectionRef.current?.existingSelection || []);
                keysInRange.forEach((key) => existingSelection.add(key));

                // 取消前一个动画帧
                if (rafIdRef.current) {
                    cancelAnimationFrame(rafIdRef.current);
                }

                // 使用requestAnimationFrame优化UI渲染
                rafIdRef.current = requestAnimationFrame(() => {
                    // 更新选中键
                    setSelectedRowKeys(Array.from(existingSelection));

                    // 更新选中行数据 - 考虑使用Map缓存提高性能
                    const newSelectedRows = [...(currentSelectionRef.current?.existingRows || [])];
                    const existingKeys = new Set(newSelectedRows.map((row) => getRecordKey(row)));

                    // 仅添加未包含的行
                    for (let i = startIdx; i <= endIdx; i++) {
                        if (i < memoizedDataSource.length) {
                            const item = memoizedDataSource[i];
                            if (isRecordSelectionDisabled(item)) continue;
                            const itemKey = getRecordKey(item, i);
                            if (!existingKeys.has(itemKey)) {
                                newSelectedRows.push(item);
                                existingKeys.add(itemKey);
                            }
                        }
                    }

                    setSelectedRows(newSelectedRows);
                    rafIdRef.current = null;
                });
            });
        },
        [isSelecting, lastSelectedIndex, startIndex, throttle, memoizedDataSource, getRecordKey, shouldHandleEvent, isRecordSelectionDisabled],
    );

    // 鼠标松开结束选择
    const handleMouseUp = useCallback(() => {
        if (isSelecting) {
            setIsSelecting(false);
            currentSelectionRef.current = null; // 清除当前选择引用
        }
    }, [isSelecting]);

    // 优化查找鼠标位置对应的表格行
    const findRowAtPosition = useCallback(
        (clientX, clientY) => {
            const tableElement = tableRef.current?.nativeElement || tableRef.current || document.querySelector(".ant-table");
            if (!tableElement) return null;

            const tableRect = tableElement.getBoundingClientRect();

            // 检查鼠标是否在表格区域内
            if (!(clientX >= tableRect.left && clientX <= tableRect.right && clientY >= tableRect.top && clientY <= tableRect.bottom)) {
                return null;
            }

            // 直接使用document.elementFromPoint获取鼠标下的元素，而不是遍历所有行
            const element = document.elementFromPoint(clientX, clientY);
            if (!element) return null;

            // 查找最近的表格行
            const rowElement = element.closest(".ant-table-row");
            if (!rowElement) return null;

            const rowKey = rowElement.getAttribute("data-row-key");
            if (!rowKey || rowKey === "add-header-row" || rowKey === "add-row") return null;

            // 使用缓存快速查找行索引
            if (rowsCache.current.has(String(rowKey))) {
                const { index } = rowsCache.current.get(String(rowKey));
                return { rowIndex: index, rowKey };
            }

            // 缓存未命中，回退到传统查找
            const rowIndex = memoizedDataSource.findIndex((item) => String(getRecordKey(item)) === String(rowKey));

            if (rowIndex >= 0) {
                return { rowIndex, rowKey };
            }

            return null;
        },
        [memoizedDataSource, getRecordKey],
    );

    // 修改全局鼠标移动处理函数
    useEffect(() => {
        // 全局鼠标移动处理
        const handleGlobalMouseMove = (e) => {
            // 如果事件源在弹出层内，不处理该事件
            if (!shouldHandleEvent(e)) {
                return;
            }

            if (isSelecting && enableGlobalTracking) {
                throttle(() => {
                    const rowInfo = findRowAtPosition(e.clientX, e.clientY);
                    if (rowInfo) {
                        handleMouseMove(e, rowInfo.rowIndex);
                    }
                });
            }
        };

        // 全局鼠标松开处理
        const handleGlobalMouseUp = () => {
            if (isSelecting) {
                handleMouseUp();
            }
        };

        // 防止文本选择 - 只在拖动选择时且鼠标在序号列区域时阻止
        const handleSelectStart = (e) => {
            if (isSelecting && e.target.closest(".row-number-with-checkbox")) {
                e.preventDefault();
                return false;
            }
        };

        // 仅在启用全局跟踪时添加全局事件监听
        if (enableGlobalTracking) {
            document.addEventListener("mousemove", handleGlobalMouseMove, { passive: true });
            document.addEventListener("selectstart", handleSelectStart);
        }

        // 全局鼠标松开总是需要监听
        document.addEventListener("mouseup", handleGlobalMouseUp);

        return () => {
            if (enableGlobalTracking) {
                document.removeEventListener("mousemove", handleGlobalMouseMove);
                document.removeEventListener("selectstart", handleSelectStart);
            }
            document.removeEventListener("mouseup", handleGlobalMouseUp);

            // 清除任何未执行的定时器和动画帧
            if (throttleTimerRef.current) {
                clearTimeout(throttleTimerRef.current);
                throttleTimerRef.current = null;
            }

            if (rafIdRef.current) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
        };
    }, [isSelecting, enableGlobalTracking, handleMouseMove, handleMouseUp, findRowAtPosition, throttle, shouldHandleEvent]);

    // 清空选择 - 同步清除，避免被其他 effect 的 cleanup 取消 RAF 导致清除失败
    const clearSelection = useCallback(() => {
        if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
        }
        setSelectedRowKeys([]);
        setSelectedRows([]);
        currentSelectionRef.current = null;
    }, []);

    // 处理行点击切换选择状态 - 使用requestAnimationFrame优化
    const toggleRowSelection = useCallback(
        (record, index) => {
            if (isRecordSelectionDisabled(record)) return;

            const key = getRecordKey(record, index);

            // 取消前一个动画帧
            if (rafIdRef.current) {
                cancelAnimationFrame(rafIdRef.current);
            }

            // 使用requestAnimationFrame优化UI更新
            rafIdRef.current = requestAnimationFrame(() => {
                setSelectedRowKeys((prev) => {
                    if (prev.includes(key)) {
                        // 如果已选中，则取消选中
                        return prev.filter((k) => k !== key);
                    } else {
                        // 如果未选中，则选中
                        return [...prev, key];
                    }
                });

                setSelectedRows((prev) => {
                    const recordExists = prev.some((row) => getRecordKey(row) === key);
                    if (recordExists) {
                        // 如果已选中，则取消选中
                        return prev.filter((row) => getRecordKey(row) !== key);
                    } else {
                        // 如果未选中，则选中
                        return [...prev, record];
                    }
                });

                rafIdRef.current = null;
            });
        },
        [getRecordKey, isRecordSelectionDisabled],
    );

    // 创建序号/选择列的渲染函数
    const renderIndexColumn = useCallback(
        (_, record, index) => {
            const disabled = isRecordSelectionDisabled(record);
            return (
                <div
                    className={`row-number-with-checkbox${disabled ? " row-number-with-checkbox--disabled" : ""}`}
                    onMouseDown={(e) => handleMouseDown(e, index)}
                    onMouseMove={(e) => handleMouseMove(e, index)}
                    onMouseUp={handleMouseUp}
                    onClick={(e) => {
                        // 阻止事件冒泡，避免触发多次事件
                        e.stopPropagation();
                        toggleRowSelection(record, index);
                    }}
                >
                    <span className={`row-number${disabled ? " row-number--disabled" : ""}`}>{(pagination.current - 1) * pagination.pageSize + index + 1}</span>
                </div>
            );
        },
        [handleMouseDown, handleMouseMove, handleMouseUp, toggleRowSelection, pagination.current, pagination.pageSize, isRecordSelectionDisabled],
    );

    // 获取表格行属性
    const getRowProps = useCallback(
        (record, index) => {
            const recordKey = getRecordKey(record, index);
            const isSelected = selectedRowKeys.includes(recordKey);
            const selectionDisabled = isRecordSelectionDisabled(record);

            return {
                className: [isSelected ? "row-selected" : "", selectionDisabled ? "row-selection-disabled" : ""].filter(Boolean).join(" "),
                // 移除对整行的鼠标事件处理，让用户可以正常选择文本
                // 只保留必要的样式类，行选择功能通过序号列处理
            };
        },
        [getRecordKey, selectedRowKeys, isRecordSelectionDisabled],
    );

    // 表格行选择配置
    const rowSelection = useMemo(
        () => ({
            selectedRowKeys,
            onChange: handleRowSelectionChange,
            columnWidth: 0, // 设为0使原复选框列不可见
            renderCell: () => null, // 不渲染默认的选择框
            columnTitle: " ",
            // 添加选择器优化
            getCheckboxProps: (record) => ({
                onClick: (e) => e.stopPropagation(), // 阻止事件冒泡
                disabled: isRecordSelectionDisabled(record),
            }),
        }),
        [selectedRowKeys, handleRowSelectionChange, isRecordSelectionDisabled],
    );

    // 获取序号/选择列定义
    const getIndexColumn = useCallback(
        (title = "序号", width = 90, options = {}) => ({
            title,
            dataIndex: "index",
            key: "index",
            width,
            ...options,
            render: renderIndexColumn,
        }),
        [renderIndexColumn],
    );

    return {
        selectedRowKeys,
        selectedRows,
        isSelecting,
        tableRef,
        rowSelection,
        getIndexColumn,
        getRowProps,
        handleRowSelectionChange,
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        clearSelection,
        toggleRowSelection,
        findRowAtPosition, // 导出此方法以便高级用例
    };
};

// 添加具名导出
export { useTableSelection };

// 默认导出
export default useTableSelection;
